/**
 * AI CONTEXT
 * Purpose: right TeacherView panel (standout moments + collapsible personal notes) in a column Flex.
 * Inputs: TeacherViewContext session/student/class maps (via useStandoutStudentList).
 * Uses: StandoutMomentPanel (quick tagging) and TeacherNotes.
 * Used by: TeacherView.
 * Refactor: none planned — list derivation lives in useStandoutStudentList.
 */
import { SearchOutlined, StarOutlined } from "@ant-design/icons"
import { Badge, Button, Checkbox, Input, Tag, Typography } from "antd"
import { useMemo, useState } from "react"

import Card from "#blocks/Card"
import CollapsibleList from "#blocks/CollapsibleList"
import Flex from "#blocks/Flex"
import { useThemeContext } from "#contexts/ThemeContextProvider"

import StandoutMomentPanel from "./StandoutMomentPanel"
import TeacherNotes from "./TeacherNotes"
import {
  STANDOUT_LIST_CAP,
  StandoutStudentItem,
  useStandoutStudentList,
} from "./useStandoutStudentList"

const { Text } = Typography

const StudentSummary = ({
  item,
  isExpanded,
}: {
  item: StandoutStudentItem
  isExpanded: boolean
}) => {
  const theme = useThemeContext()

  return (
    <Flex
      style={{
        justifyContent: "space-between",
        width: "100%",
        alignItems: "center",
        padding: "4px 0",
      }}
    >
      <Flex gap={6} style={{ alignItems: "center" }}>
        <Text
          style={{
            fontSize: 13,
            fontWeight: item.momentCount === 0 || isExpanded ? 600 : 400,
            color: isExpanded
              ? theme.eric.blue
              : item.momentCount === 0
                ? theme.eric.red
                : undefined,
          }}
        >
          {item.name}
        </Text>
        {item.source !== "roster" && (
          <Tag
            color={
              item.source === "makeup"
                ? theme.iconColors.makeups
                : theme.iconColors.tryouts
            }
            style={{ fontSize: 10, lineHeight: "16px", marginInlineEnd: 0 }}
          >
            {item.source}
          </Tag>
        )}
      </Flex>
      <Badge
        count={item.momentCount}
        showZero
        color={item.momentCount === 0 ? "red" : "blue"}
        style={{ fontSize: 11 }}
      />
    </Flex>
  )
}

const SectionLabel = ({ label, color }: { label: string; color: string }) => (
  <Text
    strong
    style={{
      fontSize: 11,
      color,
      textTransform: "uppercase",
      letterSpacing: 0.5,
      marginBottom: 2,
    }}
  >
    {label}
  </Text>
)

const StandoutMoments = () => {
  const theme = useThemeContext()
  const [searchTerm, setSearchTerm] = useState("")
  const [showAbsent, setShowAbsent] = useState(false)
  const [showAll, setShowAll] = useState(false)

  const {
    presentItems,
    absentItems,
    hiddenCount,
    eligibleCount,
    totalCount,
    isSearching,
    semester,
  } = useStandoutStudentList({ searchTerm, showAbsent, showAll })

  const StudentDetail = useMemo(() => {
    const Detail = ({
      item,
      onCollapse,
    }: {
      item: StandoutStudentItem
      onCollapse: () => void
    }) => {
      if (!item.curriculumId) return null
      return (
        <StandoutMomentPanel
          studentId={item.studentId}
          studentName={item.name}
          semester={semester}
          curriculumId={item.curriculumId}
          classId={item.classId}
          projectId={item.projectId}
          inClassSessionId={item.inClassSessionId}
          onSaved={onCollapse}
        />
      )
    }
    Detail.displayName = "StudentDetail"
    return Detail
  }, [semester])

  if (!totalCount) return null

  const showMoreVisible = !isSearching && hiddenCount > 0
  const showLessVisible =
    !isSearching && showAll && eligibleCount > STANDOUT_LIST_CAP
  const hasNoMatches =
    isSearching && !presentItems.length && !absentItems.length

  return (
    <Card style={{ marginRight: 16, width: "100%" }}>
      <Flex col gap={4} style={{ marginBottom: 12 }}>
        <Flex gap={8} style={{ alignItems: "center" }}>
          <Text strong style={{ fontSize: 16 }}>
            Standout Moments
          </Text>
          <StarOutlined />
        </Flex>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Least feedback first, current class on top
        </Text>
      </Flex>
      <Flex col gap={8} style={{ marginBottom: 12 }}>
        <Input
          size="small"
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search students..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Checkbox
          checked={showAbsent}
          onChange={(e) => setShowAbsent(e.target.checked)}
          style={{ fontSize: 12 }}
        >
          Show absent kids
        </Checkbox>
      </Flex>
      {presentItems.length > 0 && (
        <Flex col style={{ marginBottom: 8 }}>
          <SectionLabel label="Present" color={theme.eric.green} />
          <CollapsibleList
            items={presentItems}
            summary={StudentSummary}
            detail={StudentDetail}
            canExpand={(item) => !!item.curriculumId}
            separator
            noScroll
          />
        </Flex>
      )}
      {absentItems.length > 0 && (
        <Flex col style={{ marginBottom: 8 }}>
          <SectionLabel label="Absent" color={theme.eric.red} />
          <CollapsibleList
            items={absentItems}
            summary={StudentSummary}
            detail={StudentDetail}
            canExpand={(item) => !!item.curriculumId}
            separator
            noScroll
          />
        </Flex>
      )}
      {hasNoMatches && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          No students match "{searchTerm.trim()}"
        </Text>
      )}
      {(showMoreVisible || showLessVisible) && (
        <Button
          type="link"
          size="small"
          style={{ padding: 0, fontSize: 12 }}
          onClick={() => setShowAll(!showAll)}
        >
          {showMoreVisible ? `Show ${hiddenCount} more` : "Show less"}
        </Button>
      )}
    </Card>
  )
}

const TeacherClassInfo = () => {
  const { body4 } = useThemeContext()
  return (
    <Flex col style={{ height: "100%", background: body4 }}>
      <Flex
        col
        grow
        style={{
          padding: "30px 20px",
          overflowY: "auto",
          minHeight: 0,
          paddingBottom: 20,
        }}
      >
        <StandoutMoments />
      </Flex>
      <div style={{ padding: "0 20px 20px", flex: "0 0 auto" }}>
        <TeacherNotes />
      </div>
    </Flex>
  )
}
export default TeacherClassInfo
